import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

/** Blocks on the first acknowledgment this coordinator still owes, then gets out of the way. */
export const CoordinatorAcknowledgments = ({ onDone }) => {
  const [pending, setPending] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/consent/my-acknowledgments');
      setPending((res.data.required || []).filter(r => !r.accepted).map(r => r.document));
    } catch { setPending([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const current = pending[0];
  if (!current) return null;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post('/consent/acknowledge', { document_id: current.id });
      setAgreed(false);
      const rest = pending.slice(1);
      setPending(rest);
      if (rest.length === 0) {
        toast.success('Thank you — recorded.');
        onDone?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Could not record this');
    }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="rounded-2xl max-w-lg w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto [&>button]:hidden"
                     data-testid="coordinator-ack-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> {current.title}
          </DialogTitle>
          <DialogDescription>{current.summary}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {(current.points || []).map(p => (
            <p key={p} className="text-sm text-[#4B5563] leading-relaxed">• {p}</p>
          ))}
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer bg-white border border-[#E5E7EB] rounded-xl p-3">
          <Checkbox checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" data-testid="ack-agree-checkbox" />
          <span className="text-sm text-[#1F2937] leading-relaxed">{current.statement}</span>
        </label>

        <p className="text-[11px] text-[#9CA3AF]">
          Your school's Participation Agreement stays in place — this is your own acknowledgment as the
          person using the platform. We record your name, the version and the date.
        </p>

        <DialogFooter>
          <Button onClick={accept} disabled={!agreed || busy}
                  className="rounded-xl h-11 sm:h-10 w-full sm:w-auto bg-[#F97D6A] hover:bg-[#e86c5a] text-white"
                  data-testid="ack-accept-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'I accept'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CoordinatorAcknowledgments;
